import { esCopyTag } from './EsCopy';

/*
 * Numbers, money, dates, and lists in the reader's locale: the tag the copy store holds, so
 * formats follow the language the page is in, and en-US before a bundle loads or while
 * rolloutEsTranslations is off. A tag Intl refuses falls back to en-US rather than throwing.
 */

export const INTL_FALLBACK = 'en-US';

const gFormats = new Map();

function formatter( pKind, pTag, pOptions ) {
    const aTag = pTag ?? esCopyTag() ?? INTL_FALLBACK;
    const aKey = pKind.name + '\u0000' + aTag + '\u0000' + JSON.stringify( pOptions ?? {} );
    let aFormat = gFormats.get( aKey );
    if ( !aFormat ) {
        try {
            aFormat = new pKind( aTag, pOptions );
        }
        catch {
            aFormat = new pKind( INTL_FALLBACK, pOptions );
        }
        gFormats.set( aKey, aFormat );
    }
    return aFormat;
}

const toDate = ( pValue ) => {
    if ( pValue === null || pValue === undefined || pValue === '' ) return null;
    const aDate = pValue instanceof Date ? pValue
        : new Date( /^\d{4}-\d{2}-\d{2}$/.test( pValue ) ? pValue + 'T00:00:00' : pValue );
    return Number.isNaN( aDate.getTime() ) ? null : aDate;
};

/** A date, medium length by default (Oct 18, 2026; 18 oct. 2026). '' for anything not a date. */
export function esFormatDate( pValue, pOptions = { dateStyle: 'medium' }, pTag ) {
    const aDate = toDate( pValue );
    return aDate ? formatter( Intl.DateTimeFormat, pTag, pOptions ).format( aDate ) : '';
}

/** A number, grouped as the locale groups it (1,234.5; 1 234,5). '' for anything not a number. */
export function esFormatNumber( pValue, pOptions, pTag ) {
    const aNumber = Number( pValue );
    return pValue === null || pValue === undefined || Number.isNaN( aNumber ) ? ''
        : formatter( Intl.NumberFormat, pTag, pOptions ).format( aNumber );
}

/**
 * Money from integer cents, the way esback and Stripe hold it: 1999 -> $19.99, 19,99 $US.
 * pOptions joins Intl's; esPrice and esMoney pass whole-dollar digits so $35 stays $35.
 */
export function esFormatCurrency( pCents, pCurrency = 'USD', pTag, pOptions ) {
    const aCents = Number( pCents );
    return pCents === null || pCents === undefined || Number.isNaN( aCents ) ? ''
        : formatter( Intl.NumberFormat, pTag, { style: 'currency', currency: pCurrency, ...pOptions } ).format( aCents / 100 );
}

const WHOLE = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

/** Dollars from cents with the cents shown only when there are some: $35, $37.10, 37,10 $US. */
export const esFormatDollars = ( pCents, pTag ) =>
    esFormatCurrency( pCents, 'USD', pTag, Math.round( pCents ) % 100 === 0 ? WHOLE : undefined );

/** A list joined as the locale joins one: "a, b, and c"; "a, b et c". */
export function esFormatList( pItems, pOptions = { type: 'conjunction' }, pTag ) {
    return formatter( Intl.ListFormat, pTag, pOptions ).format( ( pItems ?? [] ).map( String ) );
}

// The locale's middle list separator (", " in en, "、" in ja and zh): CLDR's pattern between
// the inner items of a list, read once per tag off a three-item list's parts.
const gSeparators = new Map();

function separatorOf( pTag ) {
    const aFormat = formatter( Intl.ListFormat, pTag, { type: 'conjunction' } );
    const aKey = aFormat.resolvedOptions().locale;
    if ( !gSeparators.has( aKey ) ) {
        const aLiteral = aFormat.formatToParts( [ 'a', 'b', 'c' ] ).find( ( pPart ) => pPart.type === 'literal' );
        gSeparators.set( aKey, aLiteral?.value ?? ', ' );
    }
    return gSeparators.get( aKey );
}

/**
 * Items as the locale runs a plain series, with no "and" before the last: a pack's tables, an
 * option's equipment, the standard array ("15, 14, 13"; "15、14、13"). esFormatList is for a
 * list read as a sentence.
 */
export function esFormatSeries( pItems, pTag ) {
    return ( pItems ?? [] ).map( String ).join( separatorOf( pTag ) );
}

/** esFormatSeries for drawn items -- links, chips -- with the locale's separator as text between them. */
export function esSeriesNodes( pItems, pTag ) {
    const aSeparator = separatorOf( pTag );
    return ( pItems ?? [] ).flatMap( ( pItem, pAt ) => ( pAt > 0 ? [ aSeparator, pItem ] : [ pItem ] ) );
}
